import React, { FC } from 'react'

import { Box, useTheme } from '@mui/material'
import { ChartOptions } from 'chart.js'
import { merge } from 'lodash'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { chartOptions } from '../chartOptions'
import { EmptyData } from '../EmptyData'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'
import { SqnWidgetType } from '../types'

export const SQNWidget: FC<{
  sqnData: SqnWidgetType[] | undefined
  displayWidth: number
}> = ({ sqnData, displayWidth }) => {
  const { spacing, palette } = useTheme()
  const { t } = useTranslation()

  const chartData = {
    labels: sqnData?.map((item) => item.symbol),
    datasets: [
      {
        label: t('trades', 'Trades'),
        data: sqnData?.map((item) => Number(item.sqn)),
        backgroundColor: palette.blue[500],
        fill: true,
        borderWidth: 2,
        tension: 0.5,
        minBarLength: 3,
      },
    ],
  }

  const isDataEmpty = sqnData?.every(({ amount }) => Number(amount) === 0)
  const customTooltipLabelData = sqnData
    ? sqnData?.map(({ amount, sqn }) => ({ amount, value: sqn }))
    : []

  const defaultOptions = chartOptions({
    customTooltipLabelData: { data: customTooltipLabelData },
    isSqn: true,
  })

  const customOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        ticks: {
          callback: function (value: string | number) {
            const numericValue = typeof value === 'string' ? parseFloat(value) : value
            return numericValue.toFixed(2)
          },
        },
      },
    },
  }

  const options = merge({}, defaultOptions, customOptions)

  return (
    <StyledWidgetWrapper>
      <ReportWidgetsHeader
        text={t('sqnConfluenceReportsChartLabel')}
        explanation={t('sqnConfluenceReportsChartLabelExplanation')}
      />

      {isDataEmpty ? (
        <Box mt={4}>
          <EmptyData
            title={t('notEnoughData', 'Not enough data')}
            subtitle={t('pleaseCompleteJournalingSection', 'Please complete "Journaling" section')}
          />
        </Box>
      ) : (
        <Box ml={spacing(-3)}>
          <Bar
            data={chartData}
            options={options}
            style={{ height: spacing(42.25) }}
            key={displayWidth}
          />
        </Box>
      )}
    </StyledWidgetWrapper>
  )
}
